import React, { useState, useEffect } from 'react';
import { User, Settings } from 'lucide-react';
import { supabase } from '../lib/supabase';
import ProfileModal, { UserPreferences } from './ProfileModal';

interface ProfileButtonProps {
  email: string;
}

function ProfileButton({ email }: ProfileButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [preferences, setPreferences] = useState<UserPreferences>({
    display_name: '',
    age: undefined,
    grade: undefined,
    interests: [],
    interaction_style: undefined
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const initials = preferences.display_name
    ? preferences.display_name.slice(0, 2).toUpperCase()
    : email.split('@')[0].slice(0, 2).toUpperCase();

  useEffect(() => {
    let isMounted = true;

    async function fetchPreferences() {
      try {
        setIsLoading(true);
        const { data: userData, error: authError } = await supabase.auth.getSession();
        if (authError) throw authError;

        if (userData.session?.user.id) {
          const { data, error: profileError } = await supabase
            .from('profiles')
            .select('display_name, preferences')
            .eq('id', userData.session.user.id)
            .maybeSingle();

          if (profileError) throw profileError;
          
          if (isMounted && data) {
            setPreferences({
              display_name: data.display_name || '',
              ...data.preferences
            });
          }
        }
      } catch (error) {
        console.error('Error fetching preferences:', error);
        if (isMounted) {
          setError(error instanceof Error ? error.message : 'Failed to load preferences');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchPreferences();

    return () => {
      isMounted = false;
    };
  }, []);

  const handlePreferencesUpdate = (newPreferences: UserPreferences) => {
    setPreferences(newPreferences);
  };

  return (
    <>
      <button 
        onClick={() => setIsModalOpen(true)}
        className="relative group"
      >
        <div className="flex items-center space-x-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center text-white font-medium text-sm">
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              initials
            )}
          </div>
          <Settings className="w-4 h-4 text-slate-400 group-hover:text-slate-200 transition-colors" />
        </div>
        
        {/* Tooltip */}
        <div className="absolute right-0 mt-2 w-64 py-3 px-4 bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-left pointer-events-none">
          <div className="space-y-1">
            <p className="text-sm font-medium text-slate-200">
              {preferences.display_name || email}
            </p>
            {preferences.grade && (
              <p className="text-xs text-slate-400">
                Grade: {preferences.grade}
              </p>
            )}
            {preferences.interests && preferences.interests.length > 0 && (
              <p className="text-xs text-slate-400">
                Interests: {preferences.interests.join(', ')}
              </p>
            )}
          </div>
        </div>
      </button>

      <ProfileModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        email={email}
        initialData={preferences}
        onUpdate={handlePreferencesUpdate}
      />
    </>
  );
}

export default ProfileButton;